/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled from "styled-components";
import Label from "../Label";
import Icon from "../Icon";
import { useController } from "react-hook-form";

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: start;
  gap: 8px;
`;

const CheckboxInputWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const CheckboxIcon = styled((props) => <Icon.Check {...props} />)`
  ${(props) => props.$checked && "display: block;"}
  ${(props) => !props.$checked && "display: none;"}
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  pointer-events: none;
`;

const CheckboxInput = styled.input`
  width: 24px;
  height: 24px;
  margin: 0;
  border: 1px solid rgba(${({ theme }) => theme.colors.accent.rgba});
  border-radius: 4px;
  ${(props) => props.$error && `border-color: rgba(${props.theme.colors.error.rgba});`}
  ${(props) => props.$checked && `background: rgba(${props.theme.colors.accent.rgb}, 0.1);`}
  appearance: none;
  cursor: pointer;
`;

const CheckboxLabel = styled((props) => <Label {...props} />)`
  cursor: pointer;
`;

const Checkbox = ({
  control,
  name,
  children,
  label,
  required,
  onChange,
  defaultValue,
  disableLabelClick,
  validationRules = {},
  ...props
}) => {
  const { field, fieldState } = useController({
    name,
    control,
    rules: {
      required: required ? "error_value_is_empty" : undefined,
      ...validationRules,
    },
    defaultValue: defaultValue || false,
  });

  return (
    <CheckboxWrapper>
      <CheckboxInputWrapper>
        <CheckboxInput
          {...field}
          id={name}
          type="checkbox"
          aria-labelledby={disableLabelClick ? `${name}-label` : undefined}
          $error={fieldState.error}
          $checked={field.value}
          checked={field.value}
          onChange={(e) => {
            field.onChange(e);
            if (onChange) {
              onChange(e);
            }
          }}
          {...props}
        />
        <CheckboxIcon $checked={field.value} size={Icon.SIZE.SM} />
      </CheckboxInputWrapper>
      <CheckboxLabel id={disableLabelClick ? `${name}-label` : undefined} name={name} required={required} disableLabelClick={disableLabelClick}>
        {label ? label : children}
      </CheckboxLabel>
    </CheckboxWrapper>
  );
};

export default Checkbox;
